import { useEffect, useState } from "react";
import { Modal, Tag, Typography } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDashboard } from "../Contexts/dashboardHooks";
import type { DashboardListItem } from "../Contexts/DashboardContext";

const { Text } = Typography;

function SortableRow({ item, isFirst }: { item: DashboardListItem; isFirst: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.dashboard_id,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        background: "#fff",
        cursor: isDragging ? "grabbing" : "grab",
        boxShadow: isDragging ? "0 4px 12px rgba(0,0,0,0.12)" : "none",
        // Lift the dragged row above its siblings.
        zIndex: isDragging ? 1 : 0,
        position: "relative",
      }}
    >
      <HolderOutlined style={{ color: "rgba(0,0,0,0.3)" }} />
      <Text style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.name || "Untitled"}
      </Text>
      {isFirst && (
        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
          Loads first
        </Tag>
      )}
    </div>
  );
}

/**
 * Reorder the user's dashboards by drag-and-drop (experimental — gated by the
 * `showComponentReorderingMenu` flag). The top dashboard loads first; the order
 * is persisted per-browser via reorderDashboards → localStorage.
 */
export function DashboardReorderModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { dashboardList, reorderDashboards } = useDashboard();
  const [items, setItems] = useState<DashboardListItem[]>(dashboardList);

  // Seed from the live list each time the modal opens.
  useEffect(() => {
    if (open) setItems(dashboardList);
  }, [open, dashboardList]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((d) => d.dashboard_id === active.id);
      const newIndex = prev.findIndex((d) => d.dashboard_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const save = () => {
    reorderDashboards(items.map((d) => d.dashboard_id));
    onClose();
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={save} okText="Save order" title="Reorder Dashboards" width={480}>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Drag to reorder. The dashboard at the top loads first. The order is saved in this browser.
      </Text>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((d) => d.dashboard_id)} strategy={verticalListSortingStrategy}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map((d, i) => (
              <SortableRow key={d.dashboard_id} item={d} isFirst={i === 0} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </Modal>
  );
}
